
import React from 'react';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Calculator, TrendingUp, BarChart3, FileText, MessageSquare } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { Button } from '@/components/ui/button';
import Logo from '@/components/ui/Logo';

const BrochureFooter: React.FC = () => {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();

  const quickLinks = {
    tools: [
      { name: "Calculator", path: "/calculator", icon: Calculator },
      { name: "SmartBuy", path: "/smartbuy-analyzer", icon: TrendingUp },
      { name: "Analytics", path: "/investor-analytics", icon: BarChart3 }
    ],
    resources: [
      { name: "Checklist", path: "/checklist", icon: FileText },
      { name: "C.L.A.R.A.", path: "/chatbot", icon: MessageSquare },
      { name: "Legal", path: "/legal", icon: FileText }
    ]
  };

  return (
    <motion.footer 
      className="w-full py-12 px-4 mt-16 border-t border-border bg-muted/20"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Top Section - CTA */}
        <div className="text-center space-y-4 pb-8 border-b border-border">
          <h3 className="text-2xl font-bold">Ready to Get Started?</h3>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join thousands of confident homebuyers using EquiForge's complete platform
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/dashboard')}
            className="text-lg px-8"
          >
            Start Free Today
          </Button>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Tools</h4>
            <div className="space-y-2">
              {quickLinks.tools.map(link => (
                <Link 
                  key={link.path} 
                  to={link.path}
                  className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                >
                  <link.icon size={14} />
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
          
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Resources</h4>
            <div className="space-y-2">
              {quickLinks.resources.map(link => (
                <Link 
                  key={link.path} 
                  to={link.path}
                  className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                >
                  <link.icon size={14} />
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Company</h4>
            <div className="space-y-2">
              <Link to="/about" className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                <Home size={14} />
                About Us
              </Link>
              {isSignedIn && (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                >
                  <ArrowLeft size={14} />
                  Back to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-border space-y-4">
          <div className="flex flex-col items-center space-y-4">
            <Logo size="md" showText={true} />
            
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <Home size={16} />
              <p>EquiForge is a product of Streamwalkers™ Corporation</p>
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              Contact: info@equiforge.com | www.equiforge.com
            </p>
            
            <p className="text-xs text-muted-foreground text-center">
              © {new Date().getFullYear()} Streamwalkers Corporation. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </motion.footer>
  );
};

export default BrochureFooter;
